import { FlashList } from "@shopify/flash-list";
import React, { useMemo } from "react";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { ProductDateTrackerHistoryQuery } from "./__generated__/ProductDateTrackerHistoryQuery.graphql";
import { AHSeparator, TouchableBox, useLayoutContext } from "arkh";
import { keyExtractorId } from "../../common/keyExtractors";
import { ProductExpireBatchListItemContent } from "../ProductExpireBatchListItem/ProductExpireBatchListItem";
import { ProductDateTrackerStackScreenProps } from "./ProductDateTrackerStack";
import { ProductDateTrackerHistoryList_Store$key } from "./__generated__/ProductDateTrackerHistoryList_Store.graphql";
import { useSafeAreaInsets } from "react-native-safe-area-context";

function ProductDateTrackerHistoryList(props: {
  store: ProductDateTrackerHistoryList_Store$key;
  onPressBatch: (batchId: string) => void;
}) {
  const { data, loadNext, hasNext } = usePaginationFragment(
    graphql`
      fragment ProductDateTrackerHistoryList_Store on Store
      @refetchable(queryName: "ProductDateTrackerHistoryListPaginationQuery") {
        productExpireBatchesHistory(first: $first, after: $after)
          @connection(
            key: "ProductDateTrackerHistory__productExpireBatchesHistory"
          ) {
          edges {
            node {
              id
              ...ProductExpireBatchListItemContent_StoreProductExpireBatch
            }
          }
        }
      }
    `,
    props.store
  );

  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();

  const listData = useMemo(() => {
    const edges = data.productExpireBatchesHistory?.edges;
    return edges?.map((edge) => edge.node) ?? [];
  }, [data]);

  const renderItem = ({ item }: { item: (typeof listData)[number] }) => {
    return (
      <TouchableBox
        space="medium"
        color="surface-primary"
        onPress={() => {
          props.onPressBatch(item.id);
        }}
      >
        <ProductExpireBatchListItemContent productExpireBatch={item} />
      </TouchableBox>
    );
  };
  return (
    <FlashList
      data={listData}
      renderItem={renderItem}
      ItemSeparatorComponent={AHSeparator}
      keyExtractor={keyExtractorId}
      contentContainerStyle={{
        paddingBottom: insets.bottom + layout.space.large,
      }}
      onEndReachedThreshold={0.5}
      onEndReached={() => {
        if (hasNext) {
          loadNext(30);
        }
      }}
    />
  );
}

function InnerProductDateTrackerHistory(
  props: ProductDateTrackerStackScreenProps<"ProductDateTrackerHistory"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<ProductDateTrackerHistoryQuery>(
    graphql`
      query ProductDateTrackerHistoryQuery($first: Int, $after: String) {
        currentStore @required(action: THROW) {
          ...ProductDateTrackerHistoryList_Store
        }
      }
    `,
    { first: 50 },
    { fetchKey: props.fetchKey }
  );

  return (
    <ProductDateTrackerHistoryList
      store={data.currentStore}
      onPressBatch={(id) => {
        props.navigation.navigateDeprecated("ProductExpireBatch", {
          id,
          fromHistory: true,
        });
      }}
    />
  );
}

export function ProductDateTrackerHistory(
  props: ProductDateTrackerStackScreenProps<"ProductDateTrackerHistory">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerProductDateTrackerHistory {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
